import { useEffect, useState } from "react";
import { Button, Card, DatePicker, Space, Table, Typography, Tag, Statistic, Row, Col, Divider } from "antd";
import { DownloadOutlined, SearchOutlined, BookOutlined, RiseOutlined, FallOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Footer from "../../components/Footer";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import axiosInstance from "../../variables/api/axiosInstance";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Données simulées pour la démo
const sampleData = [
    {
        codeId: 1,
        date: "2025-09-10",
        reference: "EC-2025-001",
        libelle: "Vente marchandises",
        numeroPolice: "POL-001",
        details: [
            { codeId: 11, numeroCompte: "701000", libelle: "Vente marchandises", debit: 0, credit: 1000, journal: "VTE" },
            { codeId: 12, numeroCompte: "445710", libelle: "TVA collectée", debit: 0, credit: 180, journal: "VTE" },
            { codeId: 13, numeroCompte: "411000", libelle: "Client X", debit: 1180, credit: 0, journal: "VTE" },
        ],
    },
    {
        codeId: 2,
        date: "2025-09-11",
        reference: "EC-2025-002",
        libelle: "Vente services",
        numeroPolice: "POL-002",
        details: [
            { codeId: 21, numeroCompte: "512000", libelle: "Banque", debit: 2000, credit: 0, journal: "BNQ" },
            { codeId: 22, numeroCompte: "701000", libelle: "Vente services", debit: 0, credit: 2000, journal: "BNQ" },
        ],
    },
    {
        codeId: 3,
        date: "2025-09-12",
        reference: "EC-2025-003",
        libelle: "Achat fournitures",
        numeroPolice: "POL-003",
        details: [
            { codeId: 31, numeroCompte: "606100", libelle: "Fournitures bureau", debit: 500, credit: 0, journal: "ACH" },
            { codeId: 32, numeroCompte: "445660", libelle: "TVA déductible", debit: 90, credit: 0, journal: "ACH" },
            { codeId: 33, numeroCompte: "401000", libelle: "Fournisseur Y", debit: 0, credit: 590, journal: "ACH" },
        ],
    },
];

const displayNumbers = (num) => {
    return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(num || 0);
};

const GrandLivre = () => {
    const [periode, setPeriode] = useState([]);
    const [data, setData] = useState(sampleData);

    const handleFilter = () => {
        console.log("Période: ", periode);
    };

    useEffect(() => {
        axiosInstance.get(`/api/ecriture/grand-livre/periode`, {
            params: {
                start: periode ? periode[0] : null,
                end: periode ? periode[1] : null,
            }
        })
            .then((res) => {
                console.log(res.data);
                setData(res.data);
            }).catch((err) => {
                console.log(err);
            });
    }, [periode]);


    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Ecritures");

        // En-têtes
        const headers = ["Date", "Journal", "Compte", "Intitulé du compte", "Libellé écriture", "Débit", "Crédit"];
        worksheet.addRow(headers);

        // Styles en-tête (gras + centré)
        worksheet.getRow(1).font = { bold: true };
        worksheet.getRow(1).alignment = { horizontal: "center" };

        let totalDebit = 0;
        let totalCredit = 0;

        data.forEach((ecriture) => {
            ecriture?.details?.forEach((l) => {
                const debit = l.debit || 0;
                const credit = l.credit || 0;
                totalDebit += debit;
                totalCredit += credit;

                worksheet.addRow([
                    dayjs(ecriture.date).format("DD/MM/YYYY"),
                    l.journal || "OD",
                    l.numeroCompte,
                    l.libelle,
                    ecriture.libelle,
                    displayNumbers(debit),
                    displayNumbers(credit),
                ]);
            });
        });

        // Totaux
        const totalRow = worksheet.addRow(["", "", "", "", "Totaux", displayNumbers(totalDebit), displayNumbers(totalCredit)]);
        totalRow.font = { bold: true };

        // Ajuster largeur colonnes
        worksheet.columns.forEach((col) => {
            col.width = 20;
        });

        // Générer le fichier
        const buffer = await workbook.xlsx.writeBuffer();
        const now = dayjs().format("YYYYMMDD_HHmmss");
        saveAs(new Blob([buffer]), `ecritures_${now}.xlsx`);
    };

    // Calcul des totaux
    const totalDebit = data.reduce((acc, record) =>
        acc + record.details.reduce((sum, detail) => sum + (detail.debit || 0), 0), 0
    );
    const totalCredit = data.reduce((acc, record) =>
        acc + record.details.reduce((sum, detail) => sum + (detail.credit || 0), 0), 0
    );
    const solde = totalDebit - totalCredit;

    const columns = [
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            width: 150,
            render: (date) => (
                <Space>
                    <CalendarOutlined style={{ color: "#F39C12" }} />
                    <Text strong style={{ color: "#2D2D2D" }}>
                        {dayjs(date).format("DD/MM/YYYY")}
                    </Text>
                </Space>
            ),
        },
        {
            title: "Référence",
            dataIndex: "reference",
            key: "reference",
            width: 150,
            render: (ref) => (
                <Tag color="#FFF3E0" style={{
                    color: "#A66E00",
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    border: '1px solid #F39C12'
                }}>
                    {ref}
                </Tag>
            ),
        },
        {
            title: "Libellé",
            dataIndex: "libelle",
            key: "libelle",
            render: (_, record) => (
                <div>
                    <Text style={{ color: "#2D2D2D", fontWeight: 500 }}>
                        {record?.libelle}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        Police: {record?.numeroPolice}
                    </Text>
                </div>
            ),
        },
        {
            title: "Total Débit",
            key: "totalDebit",
            width: 150,
            align: "right",
            render: (_, record) => {
                const total = record?.details?.reduce((a, b) => a + (b.debit || 0), 0);
                return (
                    <Text strong style={{ color: "#A66E00", fontSize: '15px' }}>
                        {displayNumbers(total)} F
                    </Text>
                );
            },
        },
        {
            title: "Total Crédit",
            key: "totalCredit",
            width: 150,
            align: "right",
            render: (_, record) => {
                const total = record?.details?.reduce((a, b) => a + (b.credit || 0), 0);
                return (
                    <Text strong style={{ color: "#F39C12", fontSize: '15px' }}>
                        {displayNumbers(total)} F
                    </Text>
                );
            },
        },
    ];

    const detailColumns = [
        {
            title: "Journal",
            dataIndex: "journal",
            key: "journal",
            width: 100,
            render: (journal) => (
                <Tag color="#F39C12" style={{ fontWeight: 600 }}>
                    {journal || "OD"}
                </Tag>
            )
        },
        {
            title: "Compte",
            dataIndex: "numeroCompte",
            key: "numeroCompte",
            width: 120,
            render: (compte) => (
                <Text style={{ fontFamily: 'monospace', fontWeight: 600, color: "#2D2D2D" }}>
                    {compte}
                </Text>
            )
        },
        {
            title: "Libellé",
            dataIndex: "libelle",
            key: "libelle",
            render: (libelle) => (
                <Text style={{ color: "#2D2D2D" }}>{libelle}</Text>
            )
        },
        {
            title: "Débit",
            dataIndex: "debit",
            key: "debit",
            width: 130,
            align: "right",
            render: (debit) => (
                <Text style={{
                    color: debit > 0 ? "#A66E00" : "#999",
                    fontWeight: debit > 0 ? 600 : 400
                }}>
                    {displayNumbers(debit)} F
                </Text>
            ),
        },
        {
            title: "Crédit",
            dataIndex: "credit",
            key: "credit",
            width: 130,
            align: "right",
            render: (credit) => (
                <Text style={{
                    color: credit > 0 ? "#F39C12" : "#999",
                    fontWeight: credit > 0 ? 600 : 400
                }}>
                    {displayNumbers(credit)} F
                </Text>
            ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"COMPTABILITE"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div style={{
                            minHeight: '100vh',
                            backgroundColor: '#F2F0ED',
                            padding: '24px'
                        }}>
                            <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
                                {/* En-tête */}
                                <div style={{
                                    marginBottom: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px'
                                }}>
                                    <div style={{
                                        width: '56px',
                                        height: '56px',
                                        borderRadius: '14px',
                                        backgroundColor: '#F39C12',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 6px 16px rgba(243, 156, 18, 0.4)'
                                    }}>
                                        <BookOutlined style={{ fontSize: '28px', color: 'white' }} />
                                    </div>
                                    <div>
                                        <Title level={2} style={{
                                            margin: 0,
                                            fontSize: '32px',
                                            fontWeight: 700,
                                            color: '#2D2D2D'
                                        }}>
                                            Ecritures comptables
                                        </Title>
                                        <Text style={{
                                            color: '#666',
                                            fontSize: '15px'
                                        }}>
                                            Consultation des écritures comptables
                                        </Text>
                                    </div>
                                </div>

                                {/* Statistiques */}
                                {/* <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                    <Col xs={24} sm={8}>
                                        <Card
                                            bordered={false}
                                            style={{
                                                borderRadius: '12px',
                                                background: 'linear-gradient(135deg, #FFF3E0 0%, #ffffff 100%)',
                                                border: '2px solid #F39C12',
                                                boxShadow: '0 4px 12px rgba(243, 156, 18, 0.15)'
                                            }}
                                        >
                                            <Statistic
                                                title={<Text style={{ color: '#666', fontWeight: 500 }}>Total Débit</Text>}
                                                value={displayNumbers(totalDebit)}
                                                suffix="F CFA"
                                                valueStyle={{ color: '#A66E00', fontWeight: 700 }}
                                                prefix={<RiseOutlined />}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <Card
                                            bordered={false}
                                            style={{
                                                borderRadius: '12px',
                                                background: 'linear-gradient(135deg, #FFF3E0 0%, #ffffff 100%)',
                                                border: '2px solid #F39C12',
                                                boxShadow: '0 4px 12px rgba(243, 156, 18, 0.15)'
                                            }}
                                        >
                                            <Statistic
                                                title={<Text style={{ color: '#666', fontWeight: 500 }}>Total Crédit</Text>}
                                                value={displayNumbers(totalCredit)}
                                                suffix="F CFA"
                                                valueStyle={{ color: '#F39C12', fontWeight: 700 }}
                                                prefix={<FallOutlined />}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <Card
                                            bordered={false}
                                            style={{
                                                borderRadius: '12px',
                                                background: solde >= 0
                                                    ? 'linear-gradient(135deg, #d4edda 0%, #ffffff 100%)'
                                                    : 'linear-gradient(135deg, #f8d7da 0%, #ffffff 100%)',
                                                border: `2px solid ${solde >= 0 ? '#28a745' : '#dc3545'}`,
                                                boxShadow: `0 4px 12px ${solde >= 0 ? 'rgba(40, 167, 69, 0.15)' : 'rgba(220, 53, 69, 0.15)'}`
                                            }}
                                        >
                                            <Statistic
                                                title={<Text style={{ color: '#666', fontWeight: 500 }}>Solde</Text>}
                                                value={displayNumbers(Math.abs(solde))}
                                                suffix="F CFA"
                                                valueStyle={{
                                                    color: solde >= 0 ? '#28a745' : '#dc3545',
                                                    fontWeight: 700
                                                }}
                                                prefix={solde >= 0 ? <RiseOutlined /> : <FallOutlined />}
                                            />
                                        </Card>
                                    </Col>
                                </Row> */}

                                {/* Filtres et Actions */}
                                <Card
                                    bordered={false}
                                    style={{
                                        marginBottom: '24px',
                                        borderRadius: '12px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                    }}
                                >
                                    <Space wrap size="middle">
                                        <RangePicker
                                            onChange={(_, values) => setPeriode(values)}
                                            placeholder={["Date début", "Date fin"]}
                                            size="large"
                                            style={{
                                                borderRadius: '8px',
                                                borderColor: '#FFF3E0'
                                            }}
                                        />
                                        <Button
                                            type="primary"
                                            icon={<SearchOutlined />}
                                            onClick={handleFilter}
                                            size="large"
                                            style={{
                                                backgroundColor: '#F39C12',
                                                borderColor: '#F39C12',
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                boxShadow: '0 2px 8px rgba(243, 156, 18, 0.3)'
                                            }}
                                        >
                                            Filtrer
                                        </Button>
                                        <Button
                                            icon={<DownloadOutlined />}
                                            onClick={exportToExcel}
                                            size="large"
                                            style={{
                                                borderRadius: '8px',
                                                borderColor: '#F39C12',
                                                color: '#F39C12',
                                                fontWeight: 600
                                            }}
                                        >
                                            Exporter Excel
                                        </Button>
                                    </Space>
                                </Card>

                                {/* Tableau principal */}
                                <Card
                                    bordered={false}
                                    style={{
                                        borderRadius: '12px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <Table
                                        rowKey="codeId"
                                        columns={columns}
                                        dataSource={data}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total: ${total} écritures`
                                        }}
                                        expandable={{
                                            expandedRowRender: (record) => (
                                                <div style={{
                                                    backgroundColor: '#FFF3E0',
                                                    padding: '16px',
                                                    borderRadius: '8px',
                                                    margin: '8px 0'
                                                }}>
                                                    <Table
                                                        rowKey="codeId"
                                                        dataSource={record.details}
                                                        pagination={false}
                                                        columns={detailColumns}
                                                        size="small"
                                                        style={{
                                                            backgroundColor: 'white',
                                                            borderRadius: '6px'
                                                        }}
                                                        summary={() => {
                                                            const sumDebit = record.details.reduce((sum, d) => sum + (d.debit || 0), 0);
                                                            const sumCredit = record.details.reduce((sum, d) => sum + (d.credit || 0), 0);
                                                            return (
                                                                <>
                                                                    <Table.Summary.Row style={{ backgroundColor: '#FFF3E0' }}>
                                                                        <Table.Summary.Cell index={0} colSpan={3}>
                                                                            <Text strong style={{ color: '#2D2D2D', fontSize: '14px' }}>
                                                                                TOTAUX
                                                                            </Text>
                                                                        </Table.Summary.Cell>
                                                                        <Table.Summary.Cell index={1} align="right">
                                                                            <Text strong style={{ color: '#A66E00', fontSize: '14px' }}>
                                                                                {displayNumbers(sumDebit)} F
                                                                            </Text>
                                                                        </Table.Summary.Cell>
                                                                        <Table.Summary.Cell index={2} align="right">
                                                                            <Text strong style={{ color: '#F39C12', fontSize: '14px' }}>
                                                                                {displayNumbers(sumCredit)} F
                                                                            </Text>
                                                                        </Table.Summary.Cell>
                                                                    </Table.Summary.Row>
                                                                </>
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            ),
                                            expandIcon: ({ expanded, onExpand, record }) => (
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    onClick={e => onExpand(record, e)}
                                                    style={{
                                                        color: expanded ? '#F39C12' : '#666',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {expanded ? '− Masquer' : '+ Détails'}
                                                </Button>
                                            ),
                                        }}
                                    />
                                </Card>
                            </div>
                        </div>
                        <Footer />
                        {/* /Footer */}

                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
                {/* /Content wrapper */}
            </div>
            {/* /Layout page */}
            {/* Overlay */}
            <div className="layout-overlay layout-menu-toggle show"></div>
        </div>
    );
};

export default GrandLivre;